import React, {
  Dispatch,
  MutableRefObject,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import ContestPrizes from 'buyside/components/presentation/ContestShowPage/ContestPrizes/contestPrizes';
import Modal from 'buyside/components/presentation/Modal/modal';
import ParsedHtml from 'buyside/components/presentation/ParsedHtml/parsedHtml';

import isLocalStorageNameSupported from 'buyside/util/isLocalStorageNameSupported';
import { contestUrl } from 'buyside/util/urlHelper';

import {
  Maybe,
  SiteWideModalContestQuery,
  useSiteWideModalContestQuery
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/SiteWideModal/siteWideModalStyles.scss';

const useShowOrHideModal = (
  setShowModal: Dispatch<SetStateAction<boolean>>,
  ignoreCode: MutableRefObject<string>,
  promotedContest?: Maybe<SiteWideModalContestQuery['promotedContest']>
): void => {
  const currentDay = useRef(new Date().getDate());
  const dayCode = useRef('');

  useEffect(() => {
    if (!promotedContest || promotedContest.disableSiteWideModal) return;
    const id = promotedContest.id;
    dayCode.current = `swm-c${id}-day`;
    ignoreCode.current = `swm-c${id}-ignore`;

    const shouldHideModal =
      window.localStorage.getItem(ignoreCode.current) ||
      parseInt(String(window.localStorage.getItem(dayCode.current))) === currentDay.current;

    if (!shouldHideModal) {
      window.localStorage.setItem(dayCode.current, String(currentDay.current));
      setShowModal(true);
    }
  }, [promotedContest, ignoreCode, setShowModal]);
};

export const SiteWideModal = (): Maybe<ReactElement> => {
  const [showModal, setShowModal] = useState(false);

  const { data, loading } = useSiteWideModalContestQuery();

  const ignoreCode = useRef('');

  useShowOrHideModal(setShowModal, ignoreCode, data?.promotedContest);

  const handleDoNotShow = (): void => {
    window.localStorage.setItem(ignoreCode.current, 'true');
    setShowModal(false);
  };

  if (loading || !data?.promotedContest || !isLocalStorageNameSupported()) return null;
  const {
    id,
    active,
    name,
    promoSummary,
    prizes,
    judgesDisplayNames,
    countdownDate,
    disableSiteWideModal
  } = data.promotedContest;

  if (disableSiteWideModal) return null;
  const headerText = active ? `Enter the ${name} Now` : `Launching ${countdownDate}: ${name}`;
  const judges = judgesDisplayNames?.slice(0, 6);

  return (
    <Modal isOpen={showModal} onClose={handleDoNotShow} size="lg" title={headerText}>
      <div className={styles.swmWrapper}>
        {promoSummary && (
          <ParsedHtml html={promoSummary}>
            {({ content }): React.ReactNode => <div>{content}</div>}
          </ParsedHtml>
        )}
        {Number(judges?.length) > 0 && (
          <div>
            <h4 id="judges-heading">Judges Panel</h4>
            <ul aria-labelledby="judges-heading">
              {judges?.map(
                (name: Maybe<string>, idx: number): Maybe<React.ReactElement> => (
                  <li key={idx}>{name}</li>
                )
              )}
            </ul>
            {Number(judgesDisplayNames?.length) > 6 && (
              <Button
                type={BUTTON_TYPES.LINK}
                url={contestUrl(id)}
                text={`and ${judgesDisplayNames?.length && judgesDisplayNames?.length - 6} more...`}
              />
            )}
          </div>
        )}
        {Number(prizes?.length) > 0 ? (
          <>
            <h4>Prizes</h4>
            <ContestPrizes prizes={prizes} />
          </>
        ) : null}
        <footer className={styles.swmFooter}>
          {active && (
            <Button
              text="Enter Contest"
              type={BUTTON_TYPES.PRIMARY}
              url="/pro/research/ideas/new"
            />
          )}
          <Button text="View Contest" type={BUTTON_TYPES.SECONDARY} url={contestUrl(id)} />
          <Button text="DON'T SHOW AGAIN" type={BUTTON_TYPES.LINK} onClick={handleDoNotShow} />
        </footer>
      </div>
    </Modal>
  );
};

export default SiteWideModal;
